import React, { useRef, useState } from "react";
import { AlertCircle, ImagePlus, Trash2, X } from "lucide-react";
import { toast } from "react-toastify";
import { DealsRequest } from "../../requests/vendor/DealsRequest";
import {
  BANNER_ACCEPT,
  BANNER_MAX_SIZE_MB,
  IMAGE_GUIDES,
  PROMO_CARD_SLOTS,
  arePromoCardsComplete,
  createEmptyPromoCards,
  getPromoCardsValidationMessage,
  isPromoCardsTouched,
  normalizePromoCards,
} from "../../utils/storeDefaults";
import DimensionGuide from "./DimensionGuide";

const SLOT_LABELS = ["Left card", "Center card", "Right card"];

const PromoCardsUploader = ({ promoCards = [], onChange, disabled = false }) => {
  const inputRefs = useRef([]);
  const [uploadingSlot, setUploadingSlot] = useState(null);

  const cards = normalizePromoCards(promoCards);
  const touched = isPromoCardsTouched(cards);
  const complete = arePromoCardsComplete(cards);
  const validationMessage = getPromoCardsValidationMessage(cards);
  const uploadedCount = cards.filter((card) => card.image).length;

  const updateCards = (nextCards) => {
    onChange(normalizePromoCards(nextCards));
  };

  const updateCard = (index, patch) => {
    updateCards(cards.map((card, i) => (i === index ? { ...card, ...patch } : card)));
  };

  const processFile = async (file, slotIndex) => {
    if (!file?.type?.startsWith("image/")) {
      toast.error("Please upload a valid image file.");
      return;
    }

    if (file.size > BANNER_MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`Image must be under ${BANNER_MAX_SIZE_MB}MB.`);
      return;
    }

    setUploadingSlot(slotIndex);
    try {
      const optimized = await DealsRequest.optimizeImage(file);
      updateCard(slotIndex, { image: optimized });
    } catch {
      toast.error("Failed to process image.");
    } finally {
      setUploadingSlot(null);
    }
  };

  const clearSlot = (index) => {
    updateCard(index, { image: "", title: "", link: "" });
  };

  const clearAll = () => {
    updateCards(createEmptyPromoCards());
    toast.info("Category highlights cleared.");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-xs text-gray-500">
          Optional 3-card row below your banner and store info. If you add one, you must upload all three.
        </p>
        {touched ? (
          <button
            type="button"
            onClick={clearAll}
            disabled={disabled}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
          >
            <X className="w-3.5 h-3.5" />
            Clear all
          </button>
        ) : null}
      </div>

      <DimensionGuide guide={IMAGE_GUIDES.promoCard} />

      {validationMessage ? (
        <div className="flex items-start gap-2 px-3 py-2.5 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-xl">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{validationMessage}</span>
        </div>
      ) : null}

      {touched && !complete ? (
        <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full rounded-full bg-[#FF6C6C] transition-all duration-300"
            style={{ width: `${(uploadedCount / PROMO_CARD_SLOTS) * 100}%` }}
          />
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {cards.map((card, index) => (
          <div
            key={card.id}
            className={`rounded-2xl border p-3 transition-colors ${
              card.image
                ? "border-gray-200 bg-white"
                : touched
                ? "border-amber-200 bg-amber-50/40"
                : "border-dashed border-gray-200 bg-gray-50/60"
            }`}
          >
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              {SLOT_LABELS[index]}
            </p>

            <input
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="file"
              accept={BANNER_ACCEPT}
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) processFile(file, index);
                event.target.value = "";
              }}
            />

            {card.image ? (
              <div className="relative overflow-hidden rounded-xl aspect-[4/3] bg-gray-100 group">
                <img src={card.image} alt="" className="object-cover w-full h-full" />
                <button
                  type="button"
                  onClick={() => clearSlot(index)}
                  disabled={disabled}
                  className="absolute top-2 right-2 p-1.5 text-white rounded-lg bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/55"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => inputRefs.current[index]?.click()}
                disabled={disabled || uploadingSlot === index}
                className="flex flex-col items-center justify-center w-full rounded-xl border border-dashed border-gray-300 aspect-[4/3] bg-white hover:border-[#FF6C6C]/50 hover:bg-rose-50/30 transition-colors disabled:opacity-60"
              >
                <ImagePlus className="w-5 h-5 text-[#FF6C6C]" />
                <span className="mt-1.5 text-[11px] font-medium text-gray-500">
                  {uploadingSlot === index ? "Uploading..." : "800 × 600 px"}
                </span>
              </button>
            )}

            <input
              type="text"
              value={card.title || ""}
              onChange={(event) => updateCard(index, { title: event.target.value })}
              placeholder="Category label (optional)"
              maxLength={40}
              disabled={disabled}
              className="w-full px-3 py-2 mt-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C]"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default PromoCardsUploader;
